// domain/services/modifierRules.js
import { MAX_MODIFIERS_PER_LINE } from '../constants/modifier';
import { AUTHORED_COMPARISONS, CONDITION_FACTS, RULE_OUTCOMES } from '../constants/modifierRule';

/**
 * What a payer's conditional modifier rule MEANS: what it produces, what it stores, how long a line
 * it can make, and which rows no performer can reach.
 *
 * THE EVALUATION ITSELF IS NOT HERE — the projector applies rules, beside the other resolvers in
 * `functions/contexts/revenueCycle/`. Mirroring the matcher would be a second implementation with no
 * caller. Nor is what one EDITOR can show: that is `ui/presenters/modifierRuleAuthoring`.
 */

/** A condition nothing can evaluate is not a condition. The value is trimmed because a select can hand back ''. */
export function isConditionComplete(condition) {
  return typeof condition?.fact === 'string'
    && typeof condition?.comparison === 'string'
    && typeof condition?.value === 'string'
    && condition.value.trim().length > 0;
} // isConditionComplete()

/**
 * Does this row match everyone because it ASKS nothing?
 *
 * A `conditions` that is not a list asks something unreadable, which is the opposite — and its
 * absent `.length` read as 0 would promote the most dangerous row there is to the catch-all.
 */
function asksNothing(row) {
  const conditions = row?.conditions ?? [];

  return Array.isArray(conditions) && conditions.length === 0;
} // asksNothing()

/**
 * Is this a condition anything can evaluate? See `AUTHORED_COMPARISONS` — the projector enforces the
 * same pairings, so an unknown fact and a known fact under an unknown comparison are both unreadable.
 *
 * The `undefined` guard stops a condition carrying no comparison from matching an unknown fact's.
 */
export function isAuthoredCondition(condition) {
  const authored = AUTHORED_COMPARISONS[condition?.fact];

  return authored !== undefined && authored === condition?.comparison;
} // isAuthoredCondition()

/** The modifier codes a row produces, which is none unless it bills. */
export function ruleModifierCodes(row) {
  if (row?.result?.outcome !== RULE_OUTCOMES.BILL) {
    return [];
  }

  const modifiers = row.result.modifiers ?? [];

  return (Array.isArray(modifiers) ? modifiers : [])
    .map((modifier) => (typeof modifier?.code === 'string' ? modifier.code.trim() : ''))
    .filter((code) => code.length > 0);
} // ruleModifierCodes()

/**
 * A row nobody has started: it asks nothing and produces nothing. NOT an incomplete row — a row
 * arrives blank, so faulting it would refuse the save the instant somebody presses Add. Dropped on
 * write, like a codeless place override. A row with a condition and no modifier is a decision
 * somebody made and stopped halfway through, so it is faulted rather than dropped.
 */
export function isRuleRowBlank(row) {
  // A hold row asks nothing and earns nothing BY DESIGN — "nobody bills this code" is a whole rule.
  // Without the outcome check the write path would read a payer's refusal as empty and drop it.
  return row?.result?.outcome === RULE_OUTCOMES.BILL
    && asksNothing(row)
    && ruleModifierCodes(row).length === 0;
} // isRuleRowBlank()

/**
 * The rows nobody started: blank, AND shadowing nothing below them.
 *
 * POSITION MATTERS because the first match wins. A blank row is the ordinary state of one just
 * added, so it is dropped on write — but a blank row with a real row BENEATH it is a catch-all
 * matching everyone, and dropping that one lets the rows it was shadowing start billing.
 */
export function unstartedRuleRows(rows) {
  const list = Array.isArray(rows) ? rows : [];

  return list.reduce((unstarted, row, index) => {
    return list.slice(index).every(isRuleRowBlank) ? unstarted.add(index) : unstarted;
  }, new Set());
} // unstartedRuleRows()

/**
 * The rows that would actually be written, which is what a change and a fault are measured against.
 *
 * A LIST THAT IS NOT A LIST IS PRESERVED, not emptied: writing `[]` over a payer's configuration
 * deletes it, and the projector reads `[]` as "no rules" and bills where it should hold.
 */
export function persistedRuleRows(rows) {
  if (!Array.isArray(rows)) {
    return rows ?? [];
  }

  const unstarted = unstartedRuleRows(rows);

  return rows.filter((row, index) => !unstarted.has(index));
} // persistedRuleRows()

/**
 * The positions of rows no performer can ever reach, because a catch-all sits above them.
 *
 * Reported rather than refused at the drop: the fix is sometimes to move the catch-all instead, and
 * a refused drag does not say which.
 */
export function unreachableRuleRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  // A blank row is neither a catch-all nor stranded BY one: it is dropped on write, so it means
  // nothing either way. Skipping it in only one of the two places is what made Add fault the list.
  const catchAll = (rows ?? [])
    .findIndex((row) => !isRuleRowBlank(row) && asksNothing(row));

  if (catchAll === -1) {
    return [];
  }

  return (rows ?? []).reduce((positions, row, index) => {
    return index > catchAll && !isRuleRowBlank(row) ? positions.concat([index]) : positions;
  }, []);
} // unreachableRuleRows()

/**
 * The longest claim line this service's configuration could produce, counting distinct modifiers.
 *
 * THE WORST CASE ACROSS ROWS, not the sum: exactly one row matches. DISTINCT, because the resolver
 * de-duplicates. Either mistake refuses a configuration that is perfectly billable.
 *
 * The telehealth modifier is counted whenever the payer has one, for the reason
 * `servicesExceedingLine` already gives: nothing on a funding source says which services are
 * delivered by telehealth, and a cap one short is arguable while somebody is authoring where a fifth
 * modifier is a denial weeks later.
 */
export function worstCaseLineLength(entry, telehealthModifier) {
  const base = (entry?.modifiers ?? []).filter((code) => typeof code === 'string' && code.length > 0);
  const telehealth = typeof telehealthModifier === 'string' && telehealthModifier.length > 0
    ? [telehealthModifier]
    : [];

  const rows = Array.isArray(entry?.modifierRules) ? entry.modifierRules : [];
  const perRow = rows.length === 0 ? [[]] : rows.map(ruleModifierCodes);

  return perRow.reduce((longest, codes) => {
    const line = new Set(codes.concat(base).concat(telehealth));

    return Math.max(longest, line.size);
  }, 0);
} // worstCaseLineLength()

/** Would this service's configuration overflow a claim line? */
export function exceedsLine(entry, telehealthModifier) {
  return worstCaseLineLength(entry, telehealthModifier) > MAX_MODIFIERS_PER_LINE;
} // exceedsLine()

/** A blank row, which arrives matching everyone and producing nothing until somebody fills it in. */
export function emptyRuleRow() {
  return { conditions: [], result: { outcome: RULE_OUTCOMES.BILL, modifiers: [] } };
} // emptyRuleRow()

/**
 * The condition of one kind on a row, or null.
 *
 * The editor shows a column per kind, so it asks this question constantly; the storage is a list, so
 * nothing else in the model has an opinion about how many conditions of a kind a row carries. Taking
 * the first is what makes the two views agree without the list growing a uniqueness rule it does not
 * otherwise need.
 */
export function conditionOfKind(row, fact) {
  const conditions = row?.conditions ?? [];

  return (Array.isArray(conditions) ? conditions : [])
    .find((condition) => condition?.fact === fact) ?? null;
} // conditionOfKind()

/**
 * The row with one kind of condition set, or removed when the value is blank — the "any" answer.
 *
 * THE COMPARISON IS DERIVED FROM THE FACT: one pairing per kind is defined, and taking it as an
 * argument let a caller store a comparison nothing could evaluate.
 */
export function withCondition(row, fact, value) {
  const comparison = AUTHORED_COMPARISONS[fact];
  const others = (row?.conditions ?? []).filter((condition) => condition?.fact !== fact);
  const clean = typeof value === 'string' ? value.trim() : '';

  if (clean.length === 0) {
    return { ...row, conditions: others };
  }

  // Rebuilt in a fixed order rather than appended, so two rows meaning the same thing hold the same
  // list and the dirty check does not report a change nobody made.
  const next = others.concat([{ fact, comparison, value: clean }]);
  const order = [CONDITION_FACTS.CREDENTIAL, CONDITION_FACTS.DEGREE_LEVEL];

  return {
    ...row,
    conditions: next.sort((left, right) => order.indexOf(left.fact) - order.indexOf(right.fact)),
  };
} // withCondition()

/** The entry as it would be written, with the rows nobody started dropped. */
export function withPersistedRules(entry) {
  const rows = entry?.modifierRules;

  if (rows === undefined) {
    return entry;
  }

  const persisted = persistedRuleRows(rows);

  if (!Array.isArray(persisted)) {
    return entry;
  }

  // THE KEY IS ITSELF A DIFFERENCE: the dirty check serializes key names, and an entry that never
  // had a rule carries no `modifierRules`, so an empty list against a missing key armed Save on Add
  // alone. Deleting all the rows still reads as a change, because the baseline has them.
  if (persisted.length === 0) {
    const rest = { ...entry };

    delete rest.modifierRules;

    return rest;
  }

  return { ...entry, modifierRules: persisted };
} // withPersistedRules()
